import { isEmpty, isEqual } from "lodash";
import { SurveyBlocks } from "../surveyBlocks";
import { BlockSubTypes, BlockTypes, getBlockFullType, getBlockOption } from "./blocks";
import { Tags } from "modules/Tags/tags";
import {
  FormattingRule,
  getDefaultFormattingInstructions,
  PositionInstructions,
  DefaultMaxColumns,
  DefaultMaxRows,
} from "@coolset/cs-ui";

export enum BlockFieldTypes {
  FieldId = "fieldId",
  Title = "title",
  Layout = "displayType",
  Preview = "previewDisplayType",
  Subtitle = "subtitle",
  Tooltip = "tooltip",
  IsRequired = "isRequired",
  Options = "options",
  AllowDecimal = "isAllowDecimalPlaces",
  Range = "range",
  Content = "content",
  QuestionLayout = "layout",
  Legend = "legend",
  Tags = "tags",
  Expression = "expression",
  Fields = "fields",
  MaxRows = "maxRows",
  MaxColumns = "maxColumns",
  Type = "type",
  FormattingInstructions = "formattingInstructions",
  PositionInstructions = "positionInstructions",
  ExportInstructions = "exportInstructions",
  IsExportable = "isExportable",
}

export interface BlockValues {
  [BlockFieldTypes.FieldId]?: string;
  [BlockFieldTypes.Title]?: string;
  [BlockFieldTypes.Layout]?: string;
  [BlockFieldTypes.Preview]?: string;
  [BlockFieldTypes.Subtitle]?: string;
  [BlockFieldTypes.Tooltip]?: string;
  [BlockFieldTypes.IsRequired]?: boolean;
  [BlockFieldTypes.Options]?: string[];
  [BlockFieldTypes.AllowDecimal]?: boolean;
  [BlockFieldTypes.Range]?: RangeField;
  [BlockFieldTypes.Content]?: string;
  [BlockFieldTypes.QuestionLayout]?: string;
  [BlockFieldTypes.Legend]?: string[];
  [BlockFieldTypes.Tags]?: Tags.Tag[];
  [BlockFieldTypes.Expression]?: string;
  [BlockFieldTypes.Fields]?: string[];
  [BlockFieldTypes.MaxRows]?: number;
  [BlockFieldTypes.MaxColumns]?: number;
  [BlockFieldTypes.Type]?: "chart" | "table";
  [BlockFieldTypes.FormattingInstructions]?: FormattingRule[];
  [BlockFieldTypes.PositionInstructions]?: PositionInstructions;
  [BlockFieldTypes.ExportInstructions]?: ExportInstructions;
  [BlockFieldTypes.IsExportable]?: boolean;
}

export type RangeField = { min?: number; max?: number };

export enum SurveyLayouts {
  Wizard = "wzd",
  Simple = "smp",
  Bar = "bar",
  Line = "ln",
  Pie = "pie",
  Scatter = "sc",
  Table = "tbl",
}

export enum SurveyPreviews {
  Inline = "inl",
  Card = "crd",
}

export enum QuestionLayouts {
  Horizontal = "horizontal",
  Vertical = "vertical",
}

export type ExportInstructions = {
  name?: string;
  helperText?: string;
};

export const BlockDefaults: { [key: string]: any } = {
  [BlockFieldTypes.Title]: "New block",
  [BlockFieldTypes.Layout]: SurveyLayouts.Wizard,
  [BlockFieldTypes.Preview]: SurveyPreviews.Inline,
  [BlockFieldTypes.Subtitle]: "",
  [BlockFieldTypes.Tooltip]: "",
  [BlockFieldTypes.Content]: "",
  [BlockFieldTypes.Expression]: "",
  [BlockFieldTypes.IsRequired]: true,
  [BlockFieldTypes.Options]: [],
  [BlockFieldTypes.AllowDecimal]: false,
  rangeMin: 0,
  rangeMax: 100,
  [BlockFieldTypes.MaxRows]: DefaultMaxRows,
  [BlockFieldTypes.MaxColumns]: DefaultMaxColumns,
};

export const BlockFieldsForContentObject = [
  BlockFieldTypes.FieldId,
  BlockFieldTypes.Title,
  BlockFieldTypes.Subtitle,
  BlockFieldTypes.Tooltip,
  BlockFieldTypes.Options,
  BlockFieldTypes.AllowDecimal,
  BlockFieldTypes.Content,
  BlockFieldTypes.Layout,
  BlockFieldTypes.QuestionLayout,
  BlockFieldTypes.Legend,
  BlockFieldTypes.Expression,
  BlockFieldTypes.Fields,
  BlockFieldTypes.MaxRows,
  BlockFieldTypes.MaxColumns,
  BlockFieldTypes.Type,
  BlockFieldTypes.FormattingInstructions,
  BlockFieldTypes.PositionInstructions,
  BlockFieldTypes.ExportInstructions,
  BlockFieldTypes.IsExportable,
];

export const DefaultPreviewDisplayType = BlockDefaults[BlockFieldTypes.Preview];

export const getBlockDefaults = (blockType: BlockTypes, blockSubType?: string): BlockValues => {
  const fields = [
    ...getBlockOption(getBlockFullType(blockType, blockSubType)).fields,
    { key: BlockFieldTypes.Title, title: "Title" },
  ];

  const blockValues = fields.reduce((acc, curr) => {
    if (curr.key === BlockFieldTypes.Range) {
      return { ...acc, rangeMin: BlockDefaults.rangeMin, rangeMax: BlockDefaults.rangeMax };
    }

    if (curr.key === BlockFieldTypes.Layout) {
      if (blockSubType === BlockSubTypes.Chart) {
        return { ...acc, [curr.key]: SurveyLayouts.Bar };
      } else if (blockSubType === BlockSubTypes.Table) {
        return { ...acc, [curr.key]: SurveyLayouts.Table };
      }
    }

    if (curr.key === BlockFieldTypes.FormattingInstructions) {
      return { ...acc, [curr.key]: getDefaultFormattingInstructions(true, BlockDefaults[BlockFieldTypes.MaxRows]) };
    }

    return { ...acc, [curr.key]: BlockDefaults[curr.key] };
  }, {} as BlockValues);

  return blockValues;
};

export const blockValuesToBlock = (
  blockValues: BlockValues,
  isMainSurvey: boolean = false
): Partial<SurveyBlocks.Block> => {
  const block = { contentObject: {} } as Partial<SurveyBlocks.Block>;
  Object.entries(blockValues).forEach(([key, value]) => {
    const fieldType = key as BlockFieldTypes;
    if (fieldType === BlockFieldTypes.FieldId) {
      return;
    }

    if (fieldType === BlockFieldTypes.Layout && isMainSurvey) {
      block[BlockFieldTypes.Layout as keyof SurveyBlocks.Block] = value;
    }
    else if (fieldType === BlockFieldTypes.ExportInstructions && isMainSurvey) {
      block[BlockFieldTypes.ExportInstructions as keyof SurveyBlocks.Block] = value;
    }
    else if (BlockFieldsForContentObject.includes(fieldType)) {
      block.contentObject![fieldType as keyof SurveyBlocks.BlockContent] = value;
    } else if (fieldType === BlockFieldTypes.Range) {
      block.contentObject!.rangeMin = value?.min || BlockDefaults.rangeMin;
      block.contentObject!.rangeMax = value?.max || BlockDefaults.rangeMax;
    } else {
      block[fieldType as keyof SurveyBlocks.Block] = value;
    }
  });

  if (isEmpty(block.contentObject)) {
    delete block.contentObject;
  }

  return block;
};

export const blockToBlockValues = (block: Partial<SurveyBlocks.Block>): BlockValues => {
  const blockValues = {} as BlockValues;
  const flattenBlock = { ...block, ...block.contentObject };
  delete flattenBlock.contentObject;
  Object.entries(flattenBlock).forEach(([key, value]) => {
    if (key === "rangeMin" || key === "rangeMax") {
      blockValues[BlockFieldTypes.Range] = {
        min: flattenBlock.rangeMin,
        max: flattenBlock.rangeMax,
      };
    } else if (Object.values(BlockFieldTypes).includes(key as BlockFieldTypes)) {
      (blockValues[key as BlockFieldTypes] as any) = value;
    }
  });

  return blockValues;
};

export const removeNonChangedValues = (blockValues: BlockValues, LastBlockValues: BlockValues) => {
  const changedValues = Object.entries(blockValues).reduce((acc, [key, value]) => {
    if (!isEqual(value, LastBlockValues[key as BlockFieldTypes])) {
      return { ...acc, [key]: value };
    }
    return acc;
  }, {} as BlockValues);

  return changedValues;
};

export const isBarOrPieChart = (chartType: SurveyLayouts) => {
  return [SurveyLayouts.Bar, SurveyLayouts.Pie].includes(chartType);
};
